export type ExportPathPart = 'organization' | 'department' | 'type' | 'subtype' | 'device' | 'revision' | 'file';
export type ExportDisplayStyle = 'id' | 'label' | 'id-label';
export interface ExportPartSettings {
  style: ExportDisplayStyle;
  maxLength: number | '';
}
export type ExportPathSettings = Record<ExportPathPart, ExportPartSettings>;
export const EXPORT_PATH_PARTS: ExportPathPart[] = [
  'organization',
  'department',
  'type',
  'subtype',
  'device',
  'revision',
  'file',
];
export const EXPORT_SETTINGS_KEY = 'objekto.system-export.settings.v1';
export interface ExportSettings {
  parts: ExportPathSettings;
  pathLength: number | '';
  content: Record<string, boolean>;
}

/** Fresh defaults prevent browser preferences from mutating shared configuration. */
export function defaultExportSettings(content: Record<string, boolean>): ExportSettings {
  return {
    parts: Object.fromEntries(
      EXPORT_PATH_PARTS.map((part) => [
        part,
        {
          style: part === 'device' || part === 'revision' ? 'id-label' : 'label',
          maxLength: 60,
        },
      ])
    ) as ExportPathSettings,
    pathLength: 160,
    content: { ...content },
  };
}

/** Restore only recognized, valid preferences; a damaged browser value must not prevent export. */
export function readExportSettings(content: Record<string, boolean>): ExportSettings {
  const defaults = defaultExportSettings(content);
  try {
    const saved = JSON.parse(localStorage.getItem(EXPORT_SETTINGS_KEY) || 'null');
    if (!saved || typeof saved !== 'object') return defaults;
    if (Number.isInteger(saved.pathLength) && saved.pathLength >= 40 && saved.pathLength <= 1024) {
      defaults.pathLength = saved.pathLength;
    }
    for (const part of EXPORT_PATH_PARTS) {
      const value = saved.parts?.[part];
      if (['id', 'label', 'id-label'].includes(value?.style)) defaults.parts[part].style = value.style;
      if (Number.isInteger(value?.maxLength) && value.maxLength >= 10 && value.maxLength <= 255) {
        defaults.parts[part].maxLength = value.maxLength;
      }
    }
    for (const key of Object.keys(content)) {
      if (typeof saved.content?.[key] === 'boolean') defaults.content[key] = saved.content[key];
    }
  } catch {
    // Storage may be unavailable or contain malformed JSON; the in-memory defaults remain usable.
  }
  return defaults;
}

/** Persist preferences without turning a disabled/full storage area into an export failure. */
export function saveExportSettings(settings: ExportSettings): void {
  try {
    localStorage.setItem(EXPORT_SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    // Keep this session's settings usable even when the browser rejects persistence.
  }
}

/** Validate all editable limits before discovery or uploads. */
export function exportSettingsError({ parts, pathLength }: ExportSettings): string | null {
  if (!Number.isInteger(pathLength) || Number(pathLength) < 40 || Number(pathLength) > 1024) {
    return 'Dĺžka cesty musí byť celé číslo od 40 do 1024.';
  }
  for (const part of EXPORT_PATH_PARTS) {
    if (
      !Number.isInteger(parts[part].maxLength) ||
      Number(parts[part].maxLength) < 10 ||
      Number(parts[part].maxLength) > 255
    ) {
      return 'Dĺžka každej časti musí byť celé číslo od 10 do 255.';
    }
    if (!['id', 'label', 'id-label'].includes(parts[part].style)) return 'Vyberte platný štýl zobrazenia.';
  }
  return null;
}
